import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import {
  searchImageFileUploaded,
  setCardslistLoading,
  setIsImageUploading,
  setNavigationCluster,
  setNavigationClusterLoader,
  setSimilarityData,
  setSimilarityVertex,
} from "redux/SingleDataset/actions";
import useAllQueryParamsFilters from "./useAllQueryParamsFilters";
import { useLocation, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import {
  AUTOMATIC_FLAT_RESPONSE_PARAMETER,
  PAGE_PARAMETER,
  VERTEX_ID,
  VERTEX_SIMILARITY,
  VERTEX_TYPE,
} from "helpers/constants/miscellaneous";
import { useFeatureFlagService } from "./useFeatureFlagService";
import { VQL_UPGRADE } from "../helpers/constants/filters";
import useVqlParams from "./useVqlParams";
import { VqlQuery } from "types";
import { useUploadQueryImageApiV1DatasetDatasetIdSearchImageSimilarityUploadQueryImagePost } from "../vl-api/vlComponents";

export const useQueryImageUpload = () => {
  const uploadMutation =
    useUploadQueryImageApiV1DatasetDatasetIdSearchImageSimilarityUploadQueryImagePost();

  const uploadQueryImage = useCallback(
    async (file: File, datasetId: string): Promise<string> => {
      if (!datasetId) {
        throw new Error("Dataset ID is required for image upload");
      }

      if (file.size === 0) {
        throw new Error("Cannot upload empty file");
      }

      const formData = new FormData();
      formData.append("file", file);

      try {
        const result = await uploadMutation.mutateAsync({
          pathParams: { datasetId },
          body: formData as any, // Cast to bypass TypeScript - vlFetcher handles FormData at runtime
          headers: {
            "Content-Type": "multipart/form-data", // This triggers vlFetcher to delete the header
          },
        });

        return result.query_image_id;
      } catch (error: any) {
        throw error;
      }
    },
    [uploadMutation],
  );

  return { uploadQueryImage };
};

type HandleVisualSearchOptions = {
  flatResponse?: boolean;
};

export const useImageUpload = (onUpload?: () => void) => {
  const queryParamsFilters = useAllQueryParamsFilters();
  const { datasetId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();

  const isVQLUpgradeEnabled = useFeatureFlagService(VQL_UPGRADE).enabled;
  const { addVqlQuery } = useVqlParams();

  const [searchParams, setSearchParams] = useSearchParams();

  const [file, setFile] = useState<File | null>(null);
  const [cropData, setCropData] = useState<any>(null);

  const { uploadQueryImage } = useQueryImageUpload();

  const handleVQLVisualSearch = useCallback(
    async (cropData: any) => {
      dispatch(setIsImageUploading(false));
      dispatch(setCardslistLoading(true));
      dispatch(setSimilarityData(null));
      dispatch(setNavigationClusterLoader(true));
      dispatch(setNavigationCluster(null));
      dispatch(setSimilarityVertex(null));

      try {
        // Step 1: Upload the image to get query_image_id
        const fileToUpload = cropData.file || file!;
        const queryImageId = await uploadQueryImage(fileToUpload, datasetId!);

        // Step 2: Create VQL similarity query using the query_image_id
        const similarityQuery: VqlQuery = {
          id: crypto.randomUUID(),
          similarity: {
            op: "upload" as const,
            value: queryImageId,
            negation: false,
            ...(cropData.interacted && {
              bounding_box: [
                Math.round(cropData.x),
                Math.round(cropData.y),
                Math.round(cropData.x1),
                Math.round(cropData.y1),
              ] as [number, number, number, number],
            }),
          },
        };

        // Construct custom pathname with flat_response if enabled
        let customPathname: string | undefined;
        const currentPath = location.pathname;
        const params = new URLSearchParams(searchParams);
        params.set(AUTOMATIC_FLAT_RESPONSE_PARAMETER, "true");
        customPathname = `${currentPath}?${params.toString()}`;

        addVqlQuery(similarityQuery, customPathname);
      } catch (error) {
        console.error("Error in VQL visual search:", error);
        throw error;
      } finally {
        onUpload?.();
      }
    },
    [
      dispatch,
      file,
      uploadQueryImage,
      datasetId,
      addVqlQuery,
      location.pathname,
      searchParams,
      onUpload,
    ],
  );

  const handleVisualSearch = useCallback(
    (cropData: any, options?: HandleVisualSearchOptions) => {
      const boundingBoxValues = `${cropData.x},${cropData.y},${cropData.x1},${cropData.y1}`;
      dispatch(setIsImageUploading(false));
      dispatch(setCardslistLoading(true));
      dispatch(setSimilarityData(null));
      dispatch(setNavigationClusterLoader(true));
      dispatch(setNavigationCluster(null));
      dispatch(setSimilarityVertex(null));

      dispatch(
        searchImageFileUploaded(
          datasetId,
          file!,
          queryParamsFilters,
          cropData.file,
          cropData.interacted ? boundingBoxValues : undefined,
        ) as any,
      )
        .then((anchorMediaId: string) => {
          searchParams.set(VERTEX_ID, anchorMediaId);
          searchParams.set(VERTEX_TYPE, "UPLOAD");
          searchParams.set(PAGE_PARAMETER, "1");
          searchParams.delete(VERTEX_SIMILARITY);
          if (options?.flatResponse) {
            searchParams.set(AUTOMATIC_FLAT_RESPONSE_PARAMETER, "true");
          }
          setSearchParams(searchParams);
        })
        .catch(() => {
          searchParams.delete(VERTEX_ID);
          searchParams.delete(VERTEX_TYPE);
          searchParams.delete(VERTEX_SIMILARITY);
          if (options?.flatResponse) {
            searchParams.delete(AUTOMATIC_FLAT_RESPONSE_PARAMETER);
          }
          setSearchParams(searchParams);
          window.location.reload();
        })
        .finally(() => onUpload?.());
    },
    [
      file,
      datasetId,
      searchParams,
      queryParamsFilters,
      dispatch,
      setSearchParams,
      onUpload,
    ],
  );

  return {
    file,
    setFile,
    cropData,
    setCropData,
    handleVisualSearch,
    handleVQLVisualSearch,
    isVQLUpgradeEnabled,
  };
};
